// define ['lodash', 'backbone'], (_, Backbone) ->
import _ from 'lodash'

const INTERVAL_SIZE_STRINGS = ['second', 'minute', 'hour', 'day']
const BYTES_PER_MEGABYTE = 1048576 //Base2 kbytes (kibibytes)
// BYTES_PER_MEGABYTE = 1000000 #Base10 SI kbytes (kilobytes)


const utils = {
  flattenObject(data) {
    const result = {}
    function recurse (cur, prop) {
      if (Object(cur) !== cur) {
        result[prop] = cur
      } else if (Array.isArray(cur)) {
        for(let i = 0, l = cur.length; i < l; i++) {
          recurse(cur[i], prop + "[" + i + "]")
          if (l === 0) {
            result[prop] = []
          }
        }
      } else {
        let isEmpty = true
        for (const p in cur) {
          isEmpty = false
          recurse(cur[p], prop ? prop + "." + p : p)
        }
        if (isEmpty && prop) {
          result[prop] = {}
        }
      }
    }
    recurse(data, "")
    return result
  },

  parseAsArray(input) {
    if (input) {
      if (typeof input === 'string') {
        input = [input] //Convert to array if single string value
      }
    }
    return input || []
  },

  compactObject(obj) {
    // from http://stackoverflow.com/a/14058408
    const clone = _.clone(obj)
    _.forEach(clone, (v, k) => {
      if (!v) { delete clone[k] }
    })
    return clone
  },

  getHashCode(stringInput) {
    // from http://stackoverflow.com/a/7616484
    let hash = 0
    if (stringInput.length !== 0) {
      for (var i = 0, _len = stringInput.length; i < _len; i++) {
        const chr = stringInput.charCodeAt(i)
        hash = ((hash << 5) - hash) + chr
        hash |= 0 // Convert to 32bit integer
      }
    }
    return hash
  },

  getRetroKeyFromString(stringInput) {
    return parseFloat('0.' + Math.abs(this.getHashCode(stringInput)))
  },

  trimString(str, trimLength) {
    if (str.length > trimLength) {
      return str.substring(0, trimLength) + '\u2026'
    } else {
      return str
    }
  },

  // credit http://stackoverflow.com/a/14426477
  moveToFront(d3Selection) {
    return d3Selection.each(function() {
      this.parentNode.appendChild(this)
    })
  },

  durations(num, units = 'seconds') {
    // duration helpers. Note that these do not account for leap years or daylight savings
    let out = null
    switch (units) {
      case 'seconds':
        out = num * 1000
        break
      case 'minutes':
        out = num * 1000 * 60
        break
      case 'hours':
        out = num * 36e5
        break
      case 'days':
        out = num * 864e5
        break
      case 'weeks':
        out = num * 864e5 * 7
        break
      case 'months':
        out = num * 864e5 * 30 //always assuming 31 days... these are rough counts
        break
      case 'years':
        out = num * 864e5 * 365
        break
      default:
        out = num
    }
    return out
  },

  cloneArray(arr) {
    return arr.slice(0)
  },

  stopEvent(e) {
    e.stopPropagation()
    e.preventDefault()
    e.cancelBubble = true
    e.returnValue = false
  },

  convertMegabitsToBytes(megabits, trimDecimal = false) {
    if (!megabits) { return 0 }
    const out = megabits * BYTES_PER_MEGABYTE / 8 // Mb/s -> B/s
    return trimDecimal ? out.toFixed(1) : out
  },

  convertBytesToMegabits(bytes, trimDecimal = false) {
    if (!bytes) { return 0 }
    const out = bytes * 8 / BYTES_PER_MEGABYTE // B/s -> Mb/s
    return trimDecimal ? out.toFixed(1) : out
  },

  getPercentile(values, targetPercentile) {
    if (values && _.isArray(values) && targetPercentile) {
      const vals = values.sort((a, b) => { return a - b }) //Sort
      const percentileIndex = Math.ceil(vals.length * 0.01 * targetPercentile)
      return vals[percentileIndex]
    }
  },

  rangeToTimestamps(range) {
    return _.map(range, (t) => { return +new Date(+t) })
  },

  // Calculate the nearest interval size from an input time span in milliseconds. Eg.
  getIntervalFromTimespan(timespanMs, round = true, concat = false) {
    const _intervalSizeMillis = _.map(INTERVAL_SIZE_STRINGS, (d) => { return this.durations(1, d + 's') })
    let output = { intervalSize: 1, intervalUnit: 'hour' }
    for (var i = 0, _len = _intervalSizeMillis.length; i < _len; i++) {
      const bucket = _intervalSizeMillis[i]
      if ((bucket <= timespanMs) && (timespanMs <= (_intervalSizeMillis[i + 1] || Infinity))) {
        output = {
          intervalSize: round ? Math.ceil(timespanMs / bucket) : (timespanMs / bucket),
          intervalUnit: INTERVAL_SIZE_STRINGS[i]
        }
        break
      }
    }
    return concat ? output.intervalSize + output.intervalUnit : output
  },

  isNodeInEl(node, el) {
    while (node) {
      if (node === el) {
        return true
      }
      node = node.parentNode
    }
    return false
  },

  // TargetClass can be a single string or an array of strings (will return true if any match)
  isNodeInElByClass(node, targetClass) {
    const _multiple = _.isArray(targetClass)
    while (node && node.getAttribute) {
      const cls = node.getAttribute("class")
      if (cls) {
        if (_multiple) {
          for (var i = 0, _len = targetClass.length; i < _len; i++) {
            const tgt = targetClass[i]
            if (cls.indexOf(tgt) > -1 && new RegExp('\\b' + tgt + '\\b').test(cls)) {
              return true
            }
          }
        } else {
          if (cls.indexOf(targetClass) > -1 && new RegExp('\\b' + targetClass + '\\b').test(cls)) {
            return true
          }
        }
      }
      node = node.parentNode
    }
    return false
  },

  isNodeInElByFunction(node, testFn) {
    while (node) {
      if (testFn(node)) {
        return true
      }
      node = node.parentNode
    }
    return false
  },

  translateSvgCoordsToClient(svgEl, coordsArray) {
    // Accepts [x,y] #TODO add inverse (screen -> svg coords)
    if (svgEl && coordsArray) {
      const pt = svgEl.createSVGPoint()
      pt.x = coordsArray[0]
      pt.y = coordsArray[1]
      const pos = pt.matrixTransform(svgEl.getScreenCTM())
      return [ pos.x, pos.y ]
    }
  },

  getEventCoordinate(evt, coordName) {
    if (!evt || !coordName) return
    return evt.type.indexOf('touch') !== -1 ? evt.changedTouches[0][coordName] : evt[coordName]
  },

  stripQuotes(str="") {
    return str.replace(/"/g, '') // Replace extra quotes
  },

  getUnsignedInt (input) {
    return (new Uint32Array([+input]))[0]
  },

  getSignedInt (input) {
    return +input >> 0
  },

  // debone(backboneModelOrColl) ->
  //   if @isBackboneModelOrCollection(backboneModelOrColl)
  //     backboneModelOrColl.toJSON()
  //   else
  //     backboneModelOrColl

  // Measure the width of 'text', using the text styles from 'el'
  measureTextWidth: (() => {
    let canvas = null
    return function(text, el) {
      if (canvas == null) {
        canvas = document.createElement('canvas')
      }
      const ctx = canvas.getContext('2d')
      const styles = getComputedStyle(el, null)

      // Moz doesn't populate styles.font shorthand so have to build it from longhand props
      // TODO: fontStretch (chrome seems to choke on it)
      ctx.font = `${ styles.fontStyle } ${ styles.fontWeight } ${styles.fontVariant} ${ styles.fontSize } ${ styles.fontFamily }`

      let width = ctx.measureText(text).width
      // canvas api doesn't support letter-spacing, so add that manually
      const letterSpacing = parseFloat(styles.letterSpacing)
      if (typeof letterSpacing === 'number' && !isNaN(letterSpacing) && letterSpacing !== 0) {
        width += letterSpacing * (text.length - 1)
      }
      return width
    }
  })(),

  /**
   * Super light implementation of a subset of the Deferred pattern from
   * when/jquery/etc. using the native Promise implementation
   */
  defer() {
    const deferred = {}
    deferred.promise = new Promise((resolve, reject) => {
      deferred.resolve = resolve
      deferred.reject = reject
    })
    return deferred
  }
}

export default utils
